import {
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { NotFoundStatus, UnauthorizedStatus } from "@redotech/http/semantics";
import { countries } from "@redotech/locale/countries";
import { composeWrappers } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import {
  createAuth,
  getOrders,
  getWarranties,
  sendAuthEmail,
} from "@redotech/redo-customer-portal-app/api";
import { SetAuthContext } from "@redotech/redo-customer-portal-app/contexts/auth";
import { SettingsContext } from "@redotech/redo-customer-portal-app/contexts/settings";
import {
  SetUserContext,
  UserContext,
} from "@redotech/redo-customer-portal-app/contexts/user";
import { getResource } from "@redotech/redo-model/localization/resource";
import { Step } from "@redotech/redo-model/return-flow";
import { ReturnOptionMethod } from "@redotech/redo-model/return-flow/return-option";
import { WARRANTY_REGISTRATION_PREFIX } from "@redotech/redo-model/warranties/submit-warranty-registration-schema";
import { Button, ButtonSize, ButtonTheme } from "@redotech/redo-web/button";
import Info from "@redotech/redo-web/icon-old/info.svg";
import LeftArrow from "@redotech/redo-web/icon-old/left-arrow.svg";
import * as linkCss from "@redotech/redo-web/link.module.css";
import Logo from "@redotech/redo-web/logo.svg";
import { Spinner, SpinnerStyle } from "@redotech/redo-web/spinner";
import { AxiosError } from "axios";
import * as classNames from "classnames";
import type { FormEvent } from "react";
import {
  FormEventHandler,
  memo,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDocumentTitle } from "usehooks-ts";
import { CurrentItemBeingReturnedContext } from "../contexts/CurrentItemBeingReturnedContext";
import { ExchangeTypeContext } from "../contexts/ExchangeTypeContext";
import { ItemsReadyForReturnContext } from "../contexts/ItemsReadyForReturnContext";
import { ShippingContext } from "../contexts/ShippingContext";
import { StorefrontCartContext } from "../contexts/StorefrontCartContext";
import { CustomerWidgetWrapper } from "../customer-widget-wrapper";
import * as footer from "../order/return-flow/footer.module.css";
import { ReturnAppTextField } from "../return-app-text-field";
import {
  getCantFindOrderText,
  getHeaderText,
  getLoginButtonText,
  getLoginGiftText,
  getMissingOrderHeaderText,
  getPolicyLinkText,
  getWarrantyText,
} from "../utils/dynamic-text-utils";
import { Title } from "./common";
import * as common from "./common.module.css";
import * as loginCss from "./login.module.css";
import * as page from "./page.module.css";

export const Login = memo(function Login({ teamId }: { teamId?: string }) {
  const [params] = useSearchParams();

  const navigate = useNavigate();
  const settings = useContext(SettingsContext);

  const setUser = useContext(SetUserContext);
  const setAuth_ = useContext(SetAuthContext);
  const user = useContext(UserContext);

  const [navigating, setNavigating] = useState(false);
  const [isGift, setIsGift] = useState(false);
  const [missingRecordNumber, setMissingRecordNumber] = useState(false);
  const [recordNumber, setRecordNumber] = useState("");
  const [emailOrZip, setEmailOrZip] = useState("");

  const [firstName, setFirstName] = useState(undefined);
  const [lastName, setLastName] = useState(undefined);
  const [guestEmail, setGuestEmail] = useState(undefined);
  const [street1, setStreet1] = useState(undefined);
  const [street2, setStreet2] = useState(undefined);
  const [city, setCity] = useState(undefined);
  const [state, setState] = useState(undefined);
  const [country, setCountry] = useState("US");
  const [zip, setZip] = useState(undefined);
  const [phone, setPhone] = useState(undefined);

  const { setShopifyStorefrontCart } = useContext(StorefrontCartContext);
  const { setExchangeType } = useContext(ExchangeTypeContext);
  const { setCurrentItemBeingReturned } = useContext(
    CurrentItemBeingReturnedContext,
  );
  const { setNewOrderShippingName, setGreenReturnOverride } =
    useContext(ShippingContext);
  const { setItemsReadyForReturn } = useContext(ItemsReadyForReturnContext);

  const shouldGiftFieldsDisable = () => {
    if (!isGift) {
      return false;
    } else {
      return (
        !firstName ||
        !lastName ||
        !street1 ||
        !city ||
        !state ||
        !country ||
        !zip ||
        !phone
      );
    }
  };

  // Whenever this page is reached (via back or logout buttons) it resets data
  useEffect(() => {
    setShopifyStorefrontCart(undefined);
    setCurrentItemBeingReturned(undefined);
    setExchangeType(undefined);
    setItemsReadyForReturn([]);
    // @ts-expect-error: strict-null-checks
    setNewOrderShippingName(undefined);
    setGreenReturnOverride(undefined);
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // This is a special, temporary case for Mr. Beast refunds
  // It should be removed when they're done. The customer widget
  // is not meant to be on the login page.
  const showCustomerWidget = useMemo(() => {
    return settings?.storeUrl === "mr-beast-refunds.myshopify.com";
  }, [settings]);

  const emailToken = params.get("email_token");
  const orderTrackingToken = params.get("order_tracking_token");
  const orderTrackingFulfillmentIndex = params.get(
    "order_tracking_fulfillment_index",
  );
  const orderTrackingType = params.get("trackable_type") || "order";

  useEffect(() => {
    if (emailToken) {
      void createAuth({ token: emailToken, type: "email" }).then(
        ({ token, customer }) => {
          // @ts-expect-error: strict-null-checks
          setAuth_(token);
          // @ts-expect-error: strict-null-checks
          setUser(customer);
          const url = new URL(location.href);
          url.searchParams.delete("email_token");
          location.replace(url);
        },
      );
    }
    if (orderTrackingToken) {
      void createAuth({
        token: orderTrackingToken,
        type: "order_tracking",
      }).then(({ token, customer, orderID }) => {
        // @ts-expect-error: strict-null-checks
        setAuth_(token);
        // @ts-expect-error: strict-null-checks
        setUser(customer);
        setNavigating(true);
        if (orderTrackingType === "order") {
          navigate(
            orderTrackingFulfillmentIndex
              ? `/returns-portal/orders/${orderID}/track/${orderTrackingFulfillmentIndex}`
              : `/returns-portal/orders/${orderID}`,
            { replace: true },
          );
        } else {
          navigate(
            `/returns-portal/orders/${orderID}?redirect_to_return=true`,
            { replace: true },
          );
        }
      });
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailToken, orderTrackingToken]);

  const emailParam = params.get("email");
  const orderParam = params.get("order");

  //Auto populate the order number if it was passed into url without email param
  useLoad(async () => {
    if (orderParam && !emailParam) {
      setRecordNumber(orderParam);
    }
  }, [orderParam, emailParam]);

  const autoLoginLoad = useLoad(async () => {
    if (!emailParam || !orderParam) {
      return;
    }

    const { token, customer, orderID } = await createAuth({
      email: emailParam,
      orderNumber: orderParam,
      type: "order",
      isGift: false,
      emailOrZip: emailParam,
    });
    // @ts-expect-error: strict-null-checks
    setAuth_(token);
    // @ts-expect-error: strict-null-checks
    setUser(customer);
    setNavigating(true);

    if (next) {
      params.delete("next");
      navigate(next, { replace: true });
      return;
    }
    navigate(
      orderTrackingFulfillmentIndex
        ? `/returns-portal/orders/${orderID}/track/${orderTrackingFulfillmentIndex}`
        : `/returns-portal/orders/${orderID}`,
      { replace: true },
    );
  }, []);

  // login from merchant dashboard
  if (settings?.storeUrl === "pro-lash-x-bella-lash.myshopify.com") {
    console.log(
      "Login from merchant dashboard",
      "\n\ncustomer:",
      params.get("customer"),
      "\n\ntoken:",
      params.get("token"),
      "\n\nnext:",
      params.get("next"),
    );
  }
  const customer = params.get("customer");
  const token = params.get("token");
  const next = params.get("next");
  useEffect(() => {
    if (!customer || !token) {
      return;
    }
    // @ts-expect-error: strict-null-checks
    setAuth_(token);
    // @ts-expect-error: strict-null-checks
    setUser(JSON.parse(customer));
    const url = new URL(location.href);
    url.searchParams.delete("customer");
    url.searchParams.delete("token");
    const path = next || "/returns-portal/orders";
    navigate(path, { replace: true });
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, token]);

  useEffect(() => {
    if (!user || navigating) {
      return;
    }
    const path = next || "/returns-portal/orders";
    navigate(path, { replace: true });
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const [findOrderLoad, findOrder] = useTriggerLoad(async () => {
    if (!recordNumber || !emailOrZip) {
      return;
    } else {
      try {
        const { data } = await getOrders({
          order_number: recordNumber,
          emailOrZip: emailOrZip,
          getAllStatuses: true,
        });
        const order = data?.orders?.data[0];
        const { token, customer } = await createAuth({
          email: order.shopify.email,
          orderNumber: recordNumber,
          type: "order",
          isGift,
          emailOrZip,
        });

        // @ts-expect-error: strict-null-checks
        setAuth_(token);
        if (isGift) {
          const address = {
            street1: street1,
            street2: street2 || "",
            city: city,
            state: state,
            country: country,
            zip: zip,
            phone: phone,
            firstName: firstName,
            lastName: lastName,
          };
          // @ts-expect-error: strict-null-checks
          setUser({
            name: customer.name,
            email: customer.email,
            guestEmail: guestEmail,
            guestName: firstName + " " + lastName,
            address: JSON.stringify(address),
          });
        } else {
          const normalizedAddressFromShopify = order.shopify.shipping_address
            ? {
                street1: order.shopify.shipping_address.address1,
                street2: order.shopify.shipping_address.address2 || "",
                city: order.shopify.shipping_address.city,
                state: order.shopify.shipping_address.province,
                country: order.shopify.shipping_address.country_code,
                zip: order.shopify.shipping_address.zip,
                phone: order.shopify.shipping_address.phone,
              }
            : null;

          // @ts-expect-error: strict-null-checks
          setUser({
            ...customer,
            address: JSON.stringify(normalizedAddressFromShopify),
          });
        }
        setNavigating(true);
        navigate(`/returns-portal/orders/${order._id}`); //http://localhost:3002/widget_id/jfkaqahlqyd7kug/returns-portal/orders/65c3f880991f52bd47e4b076
      } catch (e) {
        if (
          e instanceof AxiosError &&
          e.response?.status === NotFoundStatus.code
        ) {
          throw new Error("No orders were found.");
        } else {
          throw new Error("Invalid email/zip or order number");
        }
      }
    }
  });

  // Disabling because of security concerns. Will be re-enabled when we have a OTP
  // const warrantySearchingEnabled = useMemo(
  //   () =>
  //     // @ts-expect-error: strict-null-checks
  //     settings.warranties?.registrationEnabled &&
  //     // @ts-expect-error: strict-null-checks
  //     settings.warranties?.customersCanStartClaims,
  //   // @ts-expect-error: strict-null-checks
  //   [settings.warranties],
  // );
  const warrantySearchingEnabled = false;

  const [findWarrantyLoad, findWarranty] = useTriggerLoad(async () => {
    if (!recordNumber || !emailOrZip || !warrantySearchingEnabled) {
      return;
    } else {
      try {
        const { data } = await getWarranties({
          warrantyNumber: recordNumber,
          emailOrZip: emailOrZip,
        });

        const warranty = data?.warranties[0];
        if (!warranty) {
          throw new Error("No warranties were found.");
        }
        const { token, customer } = await createAuth({
          type: "warranty",
          warrantyId: warranty._id.toString(),
        });

        // @ts-expect-error: strict-null-checks
        setAuth_(token);
        // @ts-expect-error: strict-null-checks
        setUser(customer);
        setNavigating(true);
        if (warranty.returnId) {
          navigate(`/returns-portal/warranties/${warranty.returnId}`);
        } else {
          navigate(
            `/returns-portal/warranties/${warranty._id.toString()}/warranty`,
          );
        }
      } catch (e) {
        if (
          e instanceof AxiosError &&
          e.response?.status === NotFoundStatus.code
        ) {
          throw new Error("No warranties were found.");
        } else {
          throw new Error("Invalid email/zip or warranty number");
        }
      }
    }
  });

  const handleLogin = useHandler((e: FormEvent) => {
    if (
      warrantySearchingEnabled &&
      recordNumber
        .toLowerCase()
        .startsWith(WARRANTY_REGISTRATION_PREFIX.toLowerCase())
    ) {
      findWarranty(e as any); // FIXME
    } else {
      findOrder(e as any); // FIXME
    }
  });

  const error = useMemo(() => {
    if (findOrderLoad.error) {
      return findOrderLoad.error;
    }
    if (findWarrantyLoad.error && warrantySearchingEnabled) {
      return findWarrantyLoad.error;
    }
    return null;
  }, [findOrderLoad.error, findWarrantyLoad.error, warrantySearchingEnabled]);

  const loading = useMemo(() => {
    return (
      autoLoginLoad.pending ||
      findOrderLoad.pending ||
      (warrantySearchingEnabled && findWarrantyLoad.pending)
    );
  }, [
    autoLoginLoad.pending,
    findOrderLoad.pending,
    findWarrantyLoad.pending,
    warrantySearchingEnabled,
  ]);

  return composeWrappers((inner) =>
    showCustomerWidget ? (
      <CustomerWidgetWrapper>{inner}</CustomerWidgetWrapper>
    ) : (
      inner
    ),
  )(
    <LoginDisplay
      city={city}
      country={country}
      emailOrZip={emailOrZip}
      error={error}
      firstName={firstName}
      guestEmail={guestEmail}
      isGift={isGift}
      lastName={lastName}
      loading={loading}
      missingRecordNumber={missingRecordNumber}
      onSubmit={handleLogin}
      phone={phone}
      recordNumber={recordNumber}
      setCity={setCity}
      setCountry={setCountry}
      setEmailOrZip={setEmailOrZip}
      setFirstName={setFirstName}
      setGuestEmail={setGuestEmail}
      setIsGift={setIsGift}
      setLastName={setLastName}
      setMissingRecordNumber={setMissingRecordNumber}
      setPhone={setPhone}
      setRecordNumber={setRecordNumber}
      setState={setState}
      setStreet1={setStreet1}
      setStreet2={setStreet2}
      settings={settings}
      setZip={setZip}
      shouldGiftFieldsDisable={shouldGiftFieldsDisable}
      state={state}
      street1={street1}
      street2={street2}
      warrantySearchingEnabled={warrantySearchingEnabled}
      zip={zip}
    />,
  );
});

export const LoginDisplay = (props: any) => {
  // For the preview in merchant dashboard only settings will be passed in.
  const {
    onSubmit,
    settings,
    recordNumber,
    setRecordNumber,
    isGift,
    setIsGift,
    emailOrZip,
    setEmailOrZip,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    guestEmail,
    setGuestEmail,
    street1,
    setStreet1,
    street2,
    setStreet2,
    city,
    setCity,
    state,
    setState,
    country,
    setCountry,
    zip,
    setZip,
    phone,
    setPhone,
    loading,
    shouldGiftFieldsDisable,
    error,
    readOnly = false,
    missingRecordNumber,
    setMissingRecordNumber,
    warrantySearchingEnabled,
  } = props;

  useDocumentTitle(getHeaderText(settings));
  const navigate = useNavigate();
  const handleMissingRecordNumberCancel = useHandler(() =>
    setMissingRecordNumber(false),
  );

  if (!settings) {
    return <div>Unable to get team settings.</div>;
  }

  // If the merchant only offers returns for refund then we should hide the option for returning a gift,
  // since gift returns cannot be returned for refund.
  let hideGiftOption = true;
  if (
    settings.packageProtection?.enabled &&
    settings.packageProtection.customerClaimsEnabled &&
    !settings.hideGiftOption
  ) {
    hideGiftOption = false;
  }
  if (settings.returns?.enabled && hideGiftOption) {
    const returnOptionSteps = settings.returnFlow.steps.filter(
      (step: any) => step.type === Step.RETURN,
    );
    returnOptionSteps.forEach((returnOptionStep: any) => {
      returnOptionStep.options.forEach((option: any) => {
        if (option.method.type !== ReturnOptionMethod.REFUND) {
          hideGiftOption = false;
        }
      });
    });
  }

  if (missingRecordNumber) {
    return (
      <MissingRecordNumber
        email={emailOrZip}
        onCancel={handleMissingRecordNumberCancel}
        setEmail={setEmailOrZip}
      />
    );
  }

  const textResource = getResource({
    overrides: settings.resourceOverride,
    variant: warrantySearchingEnabled ? "warranty" : undefined,
  });
  return (
    <main
      className={page.page}
      id="page"
      style={{
        backgroundImage: `url('${settings.theme.background_image}')`,
        backgroundPosition: "center",
        backgroundColor: "#F8F8F8",
      }}
    >
      {!readOnly &&
      (!settings.active || settings.returns.enableCustomerPortal === false) ? (
        <div style={{ color: settings.theme.home_text_color || "#fff" }}>
          Redo will be available very soon. Please stay tuned.
        </div>
      ) : (
        <form
          className={loginCss.loginForm}
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <div className={loginCss.logoContainer}>
            <img
              alt={`Redo Returns Portal for ${settings.merchantName}`}
              className={classNames(
                loginCss.logo,
                settings.theme?.logo_image ? "" : loginCss.hiddenButAccessible,
              )}
              src={settings.theme?.logo_image}
            />
          </div>
          <Title>{getHeaderText(settings)}</Title>
          {isGift && <h3>Buyer info</h3>}
          <div className={common.formFields}>
            <ReturnAppTextField
              fullWidth
              label={textResource.LOGIN_FIELD_ONE_LABEL}
              onChange={(e) => setEmailOrZip(e.target.value)}
              value={emailOrZip}
            />
            <ReturnAppTextField
              fullWidth
              InputProps={{
                endAdornment: !settings.theme?.hideLoginRecordNumberTooltip ? (
                  <InputAdornment position="end">
                    <CustomTooltip
                      placement="top"
                      title="Your order number can be found in your order confirmation email."
                    >
                      <IconButton>
                        <Info className={footer.infoIcon} />
                      </IconButton>
                    </CustomTooltip>
                  </InputAdornment>
                ) : null,
              }}
              label={textResource.LOGIN_FIELD_TWO_LABEL}
              onChange={(e) => setRecordNumber(e.target.value)}
              value={recordNumber}
            />
          </div>
          {/*<button*/}
          {/*  className={classNames(*/}
          {/*    loginCss.alternative,*/}
          {/*    linkCss.link,*/}
          {/*    linkCss.contrastLow,*/}
          {/*  )}*/}
          {/*  onClick={() => setMissingRecordNumber(true)}*/}
          {/*  type="button"*/}
          {/*>*/}
          {/*  Don't have an order number?*/}
          {/*</button>*/}
          {isGift && (
            <div className={common.giftFields}>
              <h3>Your info</h3>
              <div className={common.nameFields}>
                <div style={{ width: "100%" }}>
                  <ReturnAppTextField
                    fullWidth
                    label="First name"
                    onChange={(e) => setFirstName(e.target.value)}
                    value={firstName}
                  />
                </div>
                <div style={{ width: "100%" }}>
                  <ReturnAppTextField
                    fullWidth
                    label="Last name"
                    onChange={(e) => setLastName(e.target.value)}
                    value={lastName}
                  />
                </div>
              </div>
              <ReturnAppTextField
                fullWidth
                label="Email"
                onChange={(e) => setGuestEmail(e.target.value)}
                value={guestEmail}
              />
              <ReturnAppTextField
                fullWidth
                label="Address"
                onChange={(e) => setStreet1(e.target.value)}
                value={street1}
              />
              <ReturnAppTextField
                fullWidth
                label="Apartment, suite, etc. (optional)"
                onChange={(e) => setStreet2(e.target.value)}
                value={street2}
              />
              <div style={{ display: "flex", gap: "8px" }}>
                <ReturnAppTextField
                  fullWidth
                  label="City"
                  onChange={(e) => setCity(e.target.value)}
                  value={city}
                />
                <ReturnAppTextField
                  fullWidth
                  label="State/province"
                  onChange={(e) => setState(e.target.value)}
                  value={state}
                />
                <ReturnAppTextField
                  fullWidth
                  label="Zip"
                  onChange={(e) => setZip(e.target.value)}
                  value={zip}
                />
              </div>
              <FormControl>
                <InputLabel
                  id="country-dropdown-label"
                  sx={{ fontSize: "14px" }}
                >
                  Country
                </InputLabel>
                <Select
                  label="Country"
                  labelId="country-dropdown-label"
                  onChange={(e) => setCountry(e.target.value)}
                  style={{ width: "100%", borderRadius: "10px" }}
                  value={country}
                >
                  {countries.map(({ name, code }) => (
                    <MenuItem key={code} value={code}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <ReturnAppTextField
                fullWidth
                label="Phone"
                onChange={(e) => setPhone(e.target.value)}
                value={phone}
              />
            </div>
          )}
          {/*<div style={{display: "flex", justifyContent: "space-around"}}>*/}
          {!isGift && (
            <button
              className={classNames(
                loginCss.alternative,
                linkCss.link,
                linkCss.contrastLow,
              )}
              onClick={() => setMissingRecordNumber(true)}
              type="button"
              // style={{marginTop: "-24px"}}
            >
              {getCantFindOrderText(settings)}
            </button>
          )}

          {/*</div>*/}
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div style={{ width: "16px" }}>
                <Spinner style={SpinnerStyle.DARK} />
              </div>
            </div>
          ) : (
            <Button
              className={loginCss.loginButton}
              disabled={
                readOnly
                  ? false
                  : !recordNumber ||
                    !emailOrZip ||
                    emailOrZip.length < 3 ||
                    !(/\d/.test(emailOrZip) || emailOrZip.includes("@")) || // zip should have a digit, email should have a '@'
                    shouldGiftFieldsDisable()
              }
              size={ButtonSize.MEDIUM}
              // style={{marginBottom: "-20px"}}
              theme={ButtonTheme.PRIMARY}
              type="submit"
            >
              {getLoginButtonText(settings)}
            </Button>
          )}

          {error && (
            <div className={common.error}>{error.message || error.msg}</div>
          )}
          {!hideGiftOption && (
            <div className={loginCss.linkButton}>
              <button
                className={classNames(
                  loginCss.alternative,
                  linkCss.link,
                  linkCss.contrastLow,
                  loginCss.smallText,
                )}
                onClick={() => (isGift ? setIsGift(false) : setIsGift(true))}
                type="button"
              >
                {isGift ? "Not a gift?" : getLoginGiftText(settings)}
              </button>
            </div>
          )}
          {settings.warranties?.registrationEnabled && (
            <div className={loginCss.linkButton}>
              <button
                className={classNames(
                  loginCss.alternative,
                  linkCss.link,
                  linkCss.contrastLow,
                  loginCss.smallText,
                )}
                onClick={() =>
                  navigate(`/returns-portal/warranties/register`, {
                    replace: true,
                  })
                }
              >
                {getWarrantyText(settings)}
              </button>
            </div>
          )}
          <div className={loginCss.line} />
          <div
            className={loginCss.notification}
            dangerouslySetInnerHTML={{ __html: settings.theme.notification }}
          />
          <div
            className={loginCss.returnPolicy}
            onClick={() => window.open(settings.theme.privacy_link)}
          >
            {getPolicyLinkText(settings)}
          </div>
        </form>
      )}

      <StoreReturn className={page.storeReturnBlock} settings={settings} />
      {!settings.hidePortalBranding && false && (
        <div className={page.brandContainer}>
          <div
            className={page.brand}
            style={{ color: settings.theme.home_text_color || "#fff" }}
          >
            Powered by: <Logo className={page.logo} />
          </div>
        </div>
      )}
    </main>
  );
};

const CustomTooltip = styled(function CustomTooltip({
  className,
  ...props
}: any) {
  return <Tooltip {...props} classes={{ popper: className }} />;
})(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#fff",
    color: "rgba(0, 0, 0, 0.87)",
    maxWidth: 260,
    fontSize: theme.typography.pxToRem(14),
    padding: 8,
    border: "1px solid #dadde9",
  },
}));

const MissingRecordNumber = memo(function MissingRecordNumber({
  onCancel,
  email,
  setEmail,
}: {
  onCancel(): void;
  email: string;
  setEmail(value: string): void;
}) {
  const settings = useContext(SettingsContext);
  const size = useScreenType();
  const navigate = useNavigate();
  const [emailSendLoad, doEmailSend] = useTriggerLoad(async () => {
    await sendAuthEmail({ email, url: location.href });
    return true;
  });

  const handleSubmit: FormEventHandler = (e) => {
    e.preventDefault();
    doEmailSend();
  };

  return (
    <div
      className={page.page}
      id="page"
      style={{
        // @ts-expect-error: strict-null-checks
        backgroundImage: `url('${settings.theme.background_image}')`,
        backgroundPosition: "center",
        backgroundColor: "#F8F8F8",
      }}
    >
      <form className={loginCss.loginForm} onSubmit={handleSubmit}>
        <div className={loginCss.logoContainer}>
          <img
            alt=""
            className={loginCss.logo}
            // @ts-expect-error: strict-null-checks
            src={settings.theme?.logo_image}
          />
        </div>
        {/* @ts-expect-error: strict-null-checks */}
        <Title>{getMissingOrderHeaderText(settings)}</Title>
        <div className={loginCss.explanation}>
          <p>You can find the order number in your order confirmation email.</p>
          <p>
            Or, instead of looking up your order number, we can send a login
            link to your email.
          </p>
        </div>
        <ReturnAppTextField
          fullWidth
          label="Email"
          onChange={(e) => setEmail(e.target.value)}
          value={email}
        />
        <div className={loginCss.buttons}>
          <Button
            onClick={onCancel}
            size={ButtonSize.MEDIUM}
            theme={ButtonTheme.OUTLINED}
            type="button"
          >
            {size === ScreenType.MOBILE ? (
              <>
                <div>Back to</div>
                <div>order number</div>
              </>
            ) : (
              <span>Back to order number</span>
            )}
          </Button>
          <Button
            disabled={!email}
            pending={emailSendLoad.pending}
            size={ButtonSize.MEDIUM}
            theme={ButtonTheme.PRIMARY}
            type="submit"
          >
            Email me login link
          </Button>
        </div>
        {!emailSendLoad.pending &&
          emailSendLoad.error &&
          (emailSendLoad.error instanceof AxiosError &&
          emailSendLoad.error.response?.status === UnauthorizedStatus.code ? (
            <div style={{ textAlign: "center" }}>
              <p>That email does not exist on any orders.</p>
              {/* @ts-expect-error: strict-null-checks */}
              {settings.warranties?.registrationEnabled && (
                <>
                  <br />
                  <div className={loginCss.linkButton}>
                    <button
                      className={classNames(
                        loginCss.alternative,
                        linkCss.link,
                        linkCss.contrastLow,
                        loginCss.smallText,
                      )}
                      onClick={() =>
                        navigate(`/returns-portal/warranties/register`, {
                          replace: true,
                        })
                      }
                    >
                      {/* @ts-expect-error: strict-null-checks */}
                      {getWarrantyText(settings)}
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <p>There was an error sending the email.</p>
          ))}
        {emailSendLoad.value && (
          <p>An email has been sent. Please check your email inbox.</p>
        )}
      </form>
    </div>
  );
});

const StoreReturn = memo(function StoreReturn({
  className = "",
  settings,
}: {
  className?: string;
  settings?: any;
}) {
  return (
    <a
      className={classNames(className, page.storeReturn)}
      href={settings?.storeUrl ? "https://" + settings.storeUrl : ""}
      style={{ color: settings.theme.home_text_color || "#fff" }}
    >
      {/*todo change the color of this conditionally*/}
      <LeftArrow />
      <div>Back to store</div>
    </a>
  );
});
